<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import CarListingsAPI from 'dashboard/api/wgpt/carListings';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import {
  channelTypeLabelKey,
  isSupportedInbox,
} from 'dashboard/helper/wgptSocialMediaPosts';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const emit = defineEmits(['published']);

const { t } = useI18n();
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');

// Error codes from Wgpt::CarListings::SocialMediaPublisher; POST_INVALID
// carries the platform's own validation text, so it is shown as returned.
const ERROR_KEYS = {
  LISTING_NOT_PUBLISHED:
    'WGPT_CAR_LISTINGS.SOCIAL.DIALOG.ERRORS.LISTING_NOT_PUBLISHED',
  NO_CHANNELS: 'WGPT_CAR_LISTINGS.SOCIAL.DIALOG.ERRORS.NO_CHANNELS',
  INVALID_CHANNELS: 'WGPT_CAR_LISTINGS.SOCIAL.DIALOG.ERRORS.INVALID_CHANNELS',
  PHOTOS_NOT_READY: 'WGPT_CAR_LISTINGS.SOCIAL.DIALOG.ERRORS.PHOTOS_NOT_READY',
  NO_PHOTOS: 'WGPT_CAR_LISTINGS.SOCIAL.DIALOG.ERRORS.NO_PHOTOS',
  NO_VIDEO: 'WGPT_CAR_LISTINGS.SOCIAL.DIALOG.ERRORS.NO_VIDEO',
  CAPTION_REQUIRED: 'WGPT_CAR_LISTINGS.SOCIAL.DIALOG.ERRORS.CAPTION_REQUIRED',
};

const dialogRef = ref(null);
const listing = ref({});
// null until the user touches a checkbox: every connected channel is selected
// by default, even when the inbox list finishes loading after the dialog opened.
const selectedInboxIds = ref(null);
const mediaKind = ref('images');
const facebookPostType = ref('facebook_post');
const caption = ref('');
const instructions = ref('');
const showInstructions = ref(false);
const isGeneratingCaption = ref(false);
const captionFailed = ref(false);
const isPublishing = ref(false);

const eligibleInboxes = computed(() => inboxes.value.filter(isSupportedInbox));
const selectedIds = computed(
  () => selectedInboxIds.value ?? eligibleInboxes.value.map(inbox => inbox.id)
);

// Mirrors the readiness rule in WgptCarListing#generated_photos_ready?: only
// the AI images are ever posted, and not while any slot is still generating.
const photos = computed(() => listing.value.photos || []);
const hasPendingPhotos = computed(() =>
  photos.value.some(photo => photo.generation_status === 'pending')
);
const generatedPhotoCount = computed(
  () => photos.value.filter(photo => photo.generated_file_url).length
);
const imagesAvailable = computed(
  () => !hasPendingPhotos.value && generatedPhotoCount.value > 0
);
const videoAvailable = computed(() => Boolean(listing.value.video));

const imagesDisabledLabel = computed(() =>
  hasPendingPhotos.value
    ? t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.MEDIA_IMAGES_PENDING')
    : t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.MEDIA_IMAGES_NONE')
);
const imagesDisabledMessage = computed(() =>
  hasPendingPhotos.value
    ? t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.MEDIA_IMAGES_PENDING_MESSAGE')
    : t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.MEDIA_IMAGES_NONE_MESSAGE')
);

const hasFacebookSelected = computed(() =>
  eligibleInboxes.value.some(
    inbox =>
      inbox.channel_type === INBOX_TYPES.FB &&
      selectedIds.value.includes(inbox.id)
  )
);
// Images always go out as a feed post; only a Facebook video can be a reel.
const showFacebookFormat = computed(
  () => hasFacebookSelected.value && mediaKind.value === 'video'
);

const mediaAvailable = computed(() =>
  mediaKind.value === 'video' ? videoAvailable.value : imagesAvailable.value
);

const canPublish = computed(
  () =>
    selectedIds.value.length > 0 &&
    mediaAvailable.value &&
    caption.value.trim().length > 0 &&
    !isGeneratingCaption.value &&
    !isPublishing.value
);

const isSelected = inboxId => selectedIds.value.includes(inboxId);

const toggleInbox = (inboxId, checked) => {
  selectedInboxIds.value = checked
    ? [...selectedIds.value, inboxId]
    : selectedIds.value.filter(id => id !== inboxId);
};

const channelTypeText = channelType =>
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  t(channelTypeLabelKey(channelType));

// The first run sends no instructions so the server answers with the account's
// template (or the built-in default), which then seeds the instructions box.
const generateCaption = async () => {
  isGeneratingCaption.value = true;
  captionFailed.value = false;
  try {
    const { data } = await CarListingsAPI.generateSocialPostCaption(
      listing.value.id,
      { instructions: instructions.value.trim() || undefined }
    );
    caption.value = data.caption;
    if (!instructions.value.trim()) instructions.value = data.instructions;
  } catch {
    captionFailed.value = true;
  } finally {
    isGeneratingCaption.value = false;
  }
};

const open = async record => {
  listing.value = record;
  selectedInboxIds.value = null;
  facebookPostType.value = 'facebook_post';
  caption.value = '';
  instructions.value = '';
  showInstructions.value = false;
  captionFailed.value = false;
  dialogRef.value?.open();

  // Fresh media state: AI images may have finished since the list was loaded.
  try {
    const fresh = await store.dispatch('wgptCarListings/refresh', record.id);
    if (fresh) listing.value = fresh;
  } catch {
    // Keep the record we were given.
  }
  mediaKind.value =
    imagesAvailable.value || !videoAvailable.value ? 'images' : 'video';
  generateCaption();
};

const publishErrorMessage = error => {
  const { error: code, message } = error?.response?.data || {};
  if (code === 'POST_INVALID' && message) return message;
  const key = ERROR_KEYS[code];
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return key ? t(key) : t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.ERRORS.GENERIC');
};

const handlePublish = async () => {
  if (!canPublish.value) return;

  isPublishing.value = true;
  try {
    await CarListingsAPI.publishSocialPosts(listing.value.id, {
      inbox_ids: selectedIds.value,
      media_kind: mediaKind.value,
      facebook_post_type:
        mediaKind.value === 'video' ? facebookPostType.value : 'facebook_post',
      caption: caption.value.trim(),
    });
    useAlert(t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.SUCCESS'));
    emit('published');
    dialogRef.value?.close();
  } catch (error) {
    useAlert(publishErrorMessage(error));
  } finally {
    isPublishing.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    overflow-y-auto
    :title="t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.TITLE')"
    :description="listing.title"
    :confirm-button-label="t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.PUBLISH')"
    :is-loading="isPublishing"
    :disable-confirm-button="!canPublish"
    @confirm="handlePublish"
  >
    <div class="flex flex-col gap-5">
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.CHANNELS') }}
        </label>
        <p v-if="!eligibleInboxes.length" class="mb-0 text-sm text-n-slate-11">
          {{ t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.NO_CHANNELS') }}
        </p>
        <div
          v-else
          class="flex flex-col border divide-y rounded-xl border-n-weak divide-n-weak"
        >
          <label
            v-for="inbox in eligibleInboxes"
            :key="inbox.id"
            class="flex items-center gap-3 px-3 py-2 cursor-pointer"
          >
            <Checkbox
              :model-value="isSelected(inbox.id)"
              :disabled="isPublishing"
              @update:model-value="toggleInbox(inbox.id, $event)"
            />
            <ChannelIcon :inbox="inbox" class="size-4 shrink-0" />
            <span class="text-sm truncate text-n-slate-12">
              {{ inbox.name }}
            </span>
            <span class="text-xs ms-auto shrink-0 text-n-slate-11">
              {{ channelTypeText(inbox.channel_type) }}
            </span>
          </label>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.MEDIA') }}
        </label>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <RadioCard
            id="social-media-images"
            name="social_media_kind"
            :label="t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.MEDIA_IMAGES')"
            :description="
              t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.MEDIA_IMAGES_DESCRIPTION', {
                count: generatedPhotoCount,
              })
            "
            :is-active="mediaKind === 'images'"
            :disabled="!imagesAvailable"
            :disabled-label="imagesDisabledLabel"
            :disabled-message="imagesDisabledMessage"
            @select="mediaKind = 'images'"
          />
          <RadioCard
            id="social-media-video"
            name="social_media_kind"
            :label="t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.MEDIA_VIDEO')"
            :description="
              t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.MEDIA_VIDEO_DESCRIPTION')
            "
            :is-active="mediaKind === 'video'"
            :disabled="!videoAvailable"
            :disabled-label="
              t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.MEDIA_VIDEO_NONE')
            "
            :disabled-message="
              t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.MEDIA_VIDEO_NONE_MESSAGE')
            "
            @select="mediaKind = 'video'"
          />
        </div>
      </div>

      <div v-if="showFacebookFormat" class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.FACEBOOK_FORMAT') }}
        </label>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <RadioCard
            id="social-facebook-post"
            name="social_facebook_post_type"
            :label="t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.FACEBOOK_POST')"
            :description="
              t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.FACEBOOK_POST_DESCRIPTION')
            "
            :is-active="facebookPostType === 'facebook_post'"
            @select="facebookPostType = 'facebook_post'"
          />
          <RadioCard
            id="social-facebook-reel"
            name="social_facebook_post_type"
            :label="t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.FACEBOOK_REEL')"
            :description="
              t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.FACEBOOK_REEL_DESCRIPTION')
            "
            :is-active="facebookPostType === 'facebook_reel'"
            @select="facebookPostType = 'facebook_reel'"
          />
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.CAPTION') }}
          </label>
          <div class="flex items-center gap-1">
            <Button
              type="button"
              :label="
                showInstructions
                  ? t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.HIDE_INSTRUCTIONS')
                  : t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.CUSTOMIZE_INSTRUCTIONS')
              "
              icon="i-lucide-sliders-horizontal"
              variant="ghost"
              color="slate"
              size="xs"
              @click="showInstructions = !showInstructions"
            />
            <Button
              type="button"
              :label="t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.REGENERATE')"
              icon="i-lucide-refresh-cw"
              variant="ghost"
              color="slate"
              size="xs"
              :disabled="isGeneratingCaption || isPublishing"
              @click="generateCaption"
            />
          </div>
        </div>
        <TextArea
          v-if="showInstructions"
          v-model="instructions"
          :label="t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.INSTRUCTIONS')"
          :message="t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.INSTRUCTIONS_HINT')"
          :max-length="2000"
          :disabled="isGeneratingCaption || isPublishing"
          auto-height
        />
        <div
          v-if="isGeneratingCaption"
          class="flex items-center gap-2 text-sm text-n-slate-11"
        >
          <Spinner :size="16" />
          {{ t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.CAPTION_GENERATING') }}
        </div>
        <TextArea
          v-model="caption"
          :placeholder="
            t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.CAPTION_PLACEHOLDER')
          "
          :message="
            captionFailed
              ? t('WGPT_CAR_LISTINGS.SOCIAL.DIALOG.CAPTION_ERROR')
              : ''
          "
          :message-type="captionFailed ? 'error' : 'info'"
          :max-length="2200"
          :disabled="isGeneratingCaption || isPublishing"
          show-character-count
          auto-height
          max-height="20rem"
        />
      </div>
    </div>
  </Dialog>
</template>
